/*
 * Route guards.
 *
 * `RequireAuth` wraps the authenticated shell: it waits out the initial session
 * bootstrap and bounces unauthenticated visitors to /login (preserving the
 * attempted location so login can return them).
 *
 * A super-admin with no membership (principal-only token) is still admitted to
 * the shell — they manage brands from the super-admin Brands screen rather
 * than a brand-scoped console.
 *
 * `RequireBrand` is the per-route guard for brand-scoped pages (Queue, Products,
 * Credentials, run detail/report): with no active brand it redirects to the
 * super-admin home (`/brands`) so the SPA NEVER issues a brand-scoped request
 * (e.g. `GET /runs`) without a brand — which would 403 a no-membership
 * principal. A normal brand user (has a brand) passes straight through. The
 * brand-switcher (populated for the super-admin via auto-membership) is the way
 * in.
 *
 * `RequireRole` is a finer gate for capability-restricted routes; it renders a
 * fallback rather than redirecting, since the user IS authenticated, just
 * under-privileged.
 */

import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import type { Capability } from "./roles";

function SessionSpinner() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        color: "var(--color-text-faint)",
        fontSize: "var(--text-sm)",
      }}
    >
      Loading session…
    </div>
  );
}

export function RequireAuth() {
  const {
    token,
    loading,
    isAuthenticated,
    needsBrand,
    isSuperAdmin,
  } = useAuth();
  const location = useLocation();

  if (loading) return <SessionSpinner />;

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // A super-admin with no membership has a principal-only token but is still a
  // valid session — admit them to the shell (they use the Brands screen).
  if (isAuthenticated || (needsBrand && isSuperAdmin)) {
    return <Outlet />;
  }

  // Any other tokened-but-unscoped state is anomalous in the admin-provisioned
  // model (every provisioned user is a brand member) — bounce to /login.
  return <Navigate to="/login" replace state={{ from: location }} />;
}

/**
 * Per-route guard for brand-scoped pages. When the session has no active brand
 * (a super-admin with no membership selected), redirect to the super-admin home
 * (`/brands`) instead of rendering — so the page's brand-scoped queries never
 * fire. A session WITH an active brand renders its children unchanged.
 */
export function RequireBrand() {
  const { hasActiveBrand } = useAuth();
  if (!hasActiveBrand) {
    return <Navigate to="/brands" replace />;
  }
  return <Outlet />;
}

export function RequireRole({
  capability,
  fallback,
  children,
}: {
  capability: Capability;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { can } = useAuth();
  if (!can(capability)) {
    return (
      <>
        {fallback ?? (
          <p style={{ color: "var(--color-text-muted)" }}>
            You don't have permission to view this.
          </p>
        )}
      </>
    );
  }
  return <>{children}</>;
}

/**
 * Per-route guard for super-admin-only pages (Credentials). Renders a
 * not-authorized fallback for any authenticated non-super-admin; the user is
 * already authenticated + brand-scoped (this gate sits inside RequireBrand),
 * so a redirect would be confusing — a fallback message is the right response.
 */
export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) {
    return (
      <p style={{ color: "var(--color-text-muted)" }}>
        You don't have permission to view this.
      </p>
    );
  }
  return <>{children}</>;
}
